import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  FileText, Plus, Download, Send, Check, X, RefreshCw, Link as LinkIcon,
  IndianRupee, ArrowRight, Trash2, Eye,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "bg-slate-500/20 text-slate-200 border-slate-500/30",
  sent: "bg-blue-500/20 text-blue-200 border-blue-500/30",
  accepted: "bg-emerald-500/20 text-emerald-200 border-emerald-500/30",
  converted: "bg-purple-500/20 text-purple-200 border-purple-500/30",
  rejected: "bg-red-500/20 text-red-200 border-red-500/30",
  expired: "bg-amber-50 text-amber-700 border-amber-500/30",
};

const STATUS_LABEL = {
  draft: "Draft", sent: "Sent", accepted: "Accepted",
  converted: "Converted", rejected: "Rejected", expired: "Expired",
};

const EMPTY_FORM = {
  customer: { name: "", gstin: "", address: "", city: "", pin: "", email: "", phone: "" },
  plan_id: "",
  billing_cycle: "monthly",
  modules: [],
  custom_items: [],
  discount_pct: 0,
  valid_days: 30,
  notes: "",
};

const downloadFromUrl = async (url, filename) => {
  const token = localStorage.getItem("f360_token");
  const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) throw new Error("Download failed");
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = filename; a.click();
  URL.revokeObjectURL(u);
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [plans, setPlans] = useState([]);
  const [modulesCat, setModulesCat] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busyId, setBusyId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [shareLink, setShareLink] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [qr, pl, mc] = await Promise.all([
        api.get("/quotes" + (filter !== "all" ? `?status=${filter}` : "")),
        api.get("/billing/plans"),
        api.get("/billing/modules"),
      ]);
      setQuotes(qr.data);
      setPlans(pl.data);
      setModulesCat(mc.data.filter((m) => !m.is_core_module));
    } catch (e) {
      toast.error("Failed to load quotes");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const resetForm = () => { setForm(EMPTY_FORM); setEditing(null); };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer: q.customer || EMPTY_FORM.customer,
      plan_id: q.plan_id || "",
      billing_cycle: q.billing_cycle || "monthly",
      modules: (q.modules || []).map((m) => ({ module_key: m.module_key, units: m.units, rate: m.rate })),
      custom_items: (q.custom_items || []).map((c) => ({ description: c.description, quantity: c.quantity, rate: c.rate })),
      discount_pct: q.discount_pct || 0,
      valid_days: q.valid_days || 30,
      notes: q.notes || "",
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.customer.name) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        plan_id: form.plan_id || null,
        discount_pct: Number(form.discount_pct) || 0,
        valid_days: Number(form.valid_days) || 30,
        modules: form.modules.map((m) => ({ ...m, units: Number(m.units) || 0, rate: Number(m.rate) || 0 })),
        custom_items: form.custom_items.map((c) => ({ ...c, quantity: Number(c.quantity) || 0, rate: Number(c.rate) || 0 })),
      };
      if (editing) {
        await api.put(`/quotes/${editing.quote_id}`, payload);
        toast.success("Quote updated");
      } else {
        await api.post("/quotes", payload);
        toast.success("Quote created");
      }
      setShowForm(false); resetForm(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const transition = async (id, action) => {
    setBusyId(id);
    try {
      const r = await api.post(`/quotes/${id}/${action}`);
      if (action === "convert") {
        const inv = r.data?.invoice;
        toast.success(`Converted to invoice ${inv?.invoice_number}`);
      } else {
        toast.success(`Quote ${action}ed`);
      }
      load();
      if (detail?.quote_id === id) {
        const fresh = await api.get(`/quotes/${id}`);
        setDetail(fresh.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Quote deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const downloadPDF = async (q) => {
    try { await downloadFromUrl(`${API}/quotes/${q.quote_id}/pdf`, `${q.quote_number}.pdf`); }
    catch { toast.error("PDF download failed"); }
  };

  const copyShareLink = (q) => {
    const url = `${window.location.origin}/q/${q.public_token}`;
    navigator.clipboard.writeText(url);
    setShareLink(url);
    toast.success("Share link copied");
  };

  // ---- form helpers ----
  const addModuleLine = () => setForm((f) => ({
    ...f, modules: [...f.modules, { module_key: modulesCat[0]?.module_key || "", units: 1, rate: modulesCat[0]?.price_per_unit_monthly || 0 }],
  }));
  const updateModuleLine = (i, patch) => setForm((f) => {
    const next = [...f.modules]; next[i] = { ...next[i], ...patch };
    if (patch.module_key) {
      const cat = modulesCat.find((m) => m.module_key === patch.module_key);
      if (cat) next[i].rate = cat.price_per_unit_monthly;
    }
    return { ...f, modules: next };
  });
  const removeModuleLine = (i) => setForm((f) => ({ ...f, modules: f.modules.filter((_, idx) => idx !== i) }));

  const addCustomLine = () => setForm((f) => ({
    ...f, custom_items: [...f.custom_items, { description: "", quantity: 1, rate: 0 }],
  }));
  const updateCustomLine = (i, patch) => setForm((f) => {
    const next = [...f.custom_items]; next[i] = { ...next[i], ...patch }; return { ...f, custom_items: next };
  });
  const removeCustomLine = (i) => setForm((f) => ({ ...f, custom_items: f.custom_items.filter((_, idx) => idx !== i) }));

  // ---- preview totals ----
  const preview = useMemo(() => {
    const plan = plans.find((p) => p.plan_id === form.plan_id);
    const planAmt = plan ? Number(form.billing_cycle === "annual" ? plan.base_price_annual : plan.base_price_monthly) : 0;
    const mTotal = form.modules.reduce((s, m) => s + (Number(m.units) || 0) * (Number(m.rate) || 0), 0);
    const cTotal = form.custom_items.reduce((s, c) => s + (Number(c.quantity) || 0) * (Number(c.rate) || 0), 0);
    const raw = planAmt + mTotal + cTotal;
    const disc = raw * (Number(form.discount_pct) || 0) / 100;
    const sub = raw - disc;
    const gst = sub * 0.18;
    return { planAmt, mTotal, cTotal, raw, disc, sub, gst, total: sub + gst };
  }, [form, plans]);

  // ---------- RENDER ----------
  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-3">
            <FileText className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> Quotations
          </h1>
          <p className="text-sm text-gray-500 mt-1">Generate subscription quotes · Convert accepted quotes into invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 [&>option]:bg-white [&>option]:text-gray-900" data-testid="quotes-filter">
            <option value="all" style={{ background: "#0f172a", color: "#fff" }}>All statuses</option>
            <option value="draft" style={{ background: "#0f172a", color: "#fff" }}>Draft</option>
            <option value="sent" style={{ background: "#0f172a", color: "#fff" }}>Sent</option>
            <option value="accepted" style={{ background: "#0f172a", color: "#fff" }}>Accepted</option>
            <option value="converted" style={{ background: "#0f172a", color: "#fff" }}>Converted</option>
            <option value="rejected" style={{ background: "#0f172a", color: "#fff" }}>Rejected</option>
            <option value="expired" style={{ background: "#0f172a", color: "#fff" }}>Expired</option>
          </select>
          <Button onClick={load} variant="outline" className="border-white/15" data-testid="quotes-reload-btn">
            <RefreshCw className="w-4 h-4" strokeWidth={2.5} />
          </Button>
          <Button onClick={openCreate} className="f360-gradient border-0" data-testid="new-quote-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> New Quote
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="quotes-kpi">
        {["draft", "sent", "accepted", "converted"].map((s) => {
          const count = quotes.filter((q) => q.status === s).length;
          const sum = quotes.filter((q) => q.status === s).reduce((a, q) => a + (q.totals?.total || 0), 0);
          return (
            <div key={s} className="glass rounded-lg p-4">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${STATUS_COLOR[s].split(" ")[1]}`}>{STATUS_LABEL[s]}</div>
              <div className="text-2xl font-extrabold mt-1">{count}</div>
              <div className="text-xs text-gray-500">₹ {sum.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div>
            </div>
          );
        })}
      </div>

      {/* LIST */}
      <div className="glass rounded-lg p-4 overflow-x-auto" data-testid="quotes-table-wrap">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
            <tr>
              <th className="text-left py-2 px-2">Quote #</th>
              <th className="text-left py-2 px-2">Customer</th>
              <th className="text-left py-2 px-2">Plan</th>
              <th className="text-right py-2 px-2">Total</th>
              <th className="text-center py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Valid Till</th>
              <th className="text-right py-2 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {quotes.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-gray-500">No quotes yet. Click "New Quote" to create one.</td></tr>
            )}
            {quotes.map((q) => (
              <tr key={q.quote_id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`quote-row-${q.quote_number}`}>
                <td className="py-2 px-2 font-mono text-xs">{q.quote_number}</td>
                <td className="py-2 px-2">{q.customer?.name}</td>
                <td className="py-2 px-2">{q.plan_name || "—"}<span className="text-xs text-gray-500"> · {q.billing_cycle}</span></td>
                <td className="py-2 px-2 text-right font-bold">₹ {(q.totals?.total || 0).toLocaleString("en-IN")}</td>
                <td className="py-2 px-2 text-center">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${STATUS_COLOR[q.status]}`}>{STATUS_LABEL[q.status]}</span>
                </td>
                <td className="py-2 px-2 text-xs text-gray-500">{q.valid_until ? new Date(q.valid_until).toLocaleDateString() : "—"}</td>
                <td className="py-2 px-2 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <button title="View" onClick={() => setDetail(q)} className="text-gray-600 hover:text-gray-900" data-testid={`view-quote-${q.quote_number}`}>
                      <Eye className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <button title="Download PDF" onClick={() => downloadPDF(q)} className="text-orange-300 hover:text-gray-900" data-testid={`pdf-quote-${q.quote_number}`}>
                      <Download className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                    <button title="Copy share link" onClick={() => copyShareLink(q)} className="text-blue-300 hover:text-gray-900" data-testid={`share-quote-${q.quote_number}`}>
                      <LinkIcon className="w-4 h-4" />
                    </button>
                    {q.status === "draft" && (
                      <button title="Send" disabled={busyId === q.quote_id} onClick={() => transition(q.quote_id, "send")} className="text-blue-300 hover:text-gray-900" data-testid={`send-quote-${q.quote_number}`}>
                        <Send className="w-4 h-4" />
                      </button>
                    )}
                    {q.status === "sent" && (
                      <>
                        <button title="Accept" onClick={() => transition(q.quote_id, "accept")} className="text-emerald-300 hover:text-gray-900" data-testid={`accept-quote-${q.quote_number}`}>
                          <Check className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                        <button title="Reject" onClick={() => transition(q.quote_id, "reject")} className="text-red-300 hover:text-gray-900" data-testid={`reject-quote-${q.quote_number}`}>
                          <X className="w-4 h-4" strokeWidth={2.5} />
                        </button>
                      </>
                    )}
                    {q.status === "accepted" && (
                      <button title="Convert to Invoice" onClick={() => transition(q.quote_id, "convert")} className="text-purple-300 hover:text-white inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 text-xs" data-testid={`convert-quote-${q.quote_number}`}>
                        <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} /> Invoice
                      </button>
                    )}
                    {(q.status === "draft" || q.status === "expired" || q.status === "rejected") && (
                      <button title="Delete" onClick={() => remove(q.quote_id)} className="text-red-300 hover:text-gray-900" data-testid={`delete-quote-${q.quote_number}`}>
                        <Trash2 className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CREATE/EDIT MODAL */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-full max-w-4xl bg-white border border-gray-200 rounded-lg p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="quote-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{editing ? `Edit Quote ${editing.quote_number}` : "New Quotation"}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>

            {/* Customer */}
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-2">Customer</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Input placeholder="Company Name *" value={form.customer.name} onChange={(e) => setForm({ ...form, customer: { ...form.customer, name: e.target.value } })} data-testid="qf-customer-name" />
                <Input placeholder="GSTIN" value={form.customer.gstin} onChange={(e) => setForm({ ...form, customer: { ...form.customer, gstin: e.target.value } })} data-testid="qf-customer-gstin" />
                <Input placeholder="Address" value={form.customer.address} onChange={(e) => setForm({ ...form, customer: { ...form.customer, address: e.target.value } })} data-testid="qf-customer-address" />
                <Input placeholder="City" value={form.customer.city} onChange={(e) => setForm({ ...form, customer: { ...form.customer, city: e.target.value } })} data-testid="qf-customer-city" />
                <Input placeholder="PIN" value={form.customer.pin} onChange={(e) => setForm({ ...form, customer: { ...form.customer, pin: e.target.value } })} />
                <Input placeholder="Email" value={form.customer.email} onChange={(e) => setForm({ ...form, customer: { ...form.customer, email: e.target.value } })} />
                <Input placeholder="Phone" value={form.customer.phone} onChange={(e) => setForm({ ...form, customer: { ...form.customer, phone: e.target.value } })} />
              </div>
            </div>

            {/* Plan */}
            <div className="mb-5">
              <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-2">Plan</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 [&>option]:bg-white [&>option]:text-gray-900" data-testid="qf-plan">
                  <option value="" style={{ background: "#0f172a", color: "#fff" }}>No plan (modules only)</option>
                  {plans.map((p) => (
                    <option key={p.plan_id} value={p.plan_id} style={{ background: "#0f172a", color: "#fff" }}>{p.name} — ₹{p.base_price_monthly}/mo</option>
                  ))}
                </select>
                <select value={form.billing_cycle} onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
                  className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 [&>option]:bg-white [&>option]:text-gray-900" data-testid="qf-cycle">
                  <option value="monthly" style={{ background: "#0f172a", color: "#fff" }}>Monthly</option>
                  <option value="annual" style={{ background: "#0f172a", color: "#fff" }}>Annual (2 months free)</option>
                </select>
              </div>
            </div>

            {/* Modules */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Add-on Modules</div>
                <Button size="sm" variant="outline" onClick={addModuleLine} className="border-white/15 text-xs" data-testid="qf-add-module"><Plus className="w-3 h-3 mr-1" strokeWidth={2.5} />Module</Button>
              </div>
              {form.modules.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                  <select value={m.module_key} onChange={(e) => updateModuleLine(i, { module_key: e.target.value })}
                    className="col-span-6 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 [&>option]:bg-white [&>option]:text-gray-900">
                    {modulesCat.map((mc) => <option key={mc.module_key} value={mc.module_key} style={{ background: "#0f172a", color: "#fff" }}>{mc.module_name}</option>)}
                  </select>
                  <Input type="number" min="0" placeholder="Units" value={m.units} onChange={(e) => updateModuleLine(i, { units: e.target.value })} className="col-span-2" />
                  <Input type="number" min="0" step="0.01" placeholder="Rate" value={m.rate} onChange={(e) => updateModuleLine(i, { rate: e.target.value })} className="col-span-3" />
                  <button onClick={() => removeModuleLine(i)} className="col-span-1 text-red-300 hover:text-gray-900 flex justify-center"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                </div>
              ))}
            </div>

            {/* Custom items */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold">Custom Items</div>
                <Button size="sm" variant="outline" onClick={addCustomLine} className="border-white/15 text-xs" data-testid="qf-add-custom"><Plus className="w-3 h-3 mr-1" strokeWidth={2.5} />Item</Button>
              </div>
              {form.custom_items.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                  <Input placeholder="Description" value={c.description} onChange={(e) => updateCustomLine(i, { description: e.target.value })} className="col-span-6" />
                  <Input type="number" min="0" step="0.01" placeholder="Qty" value={c.quantity} onChange={(e) => updateCustomLine(i, { quantity: e.target.value })} className="col-span-2" />
                  <Input type="number" min="0" step="0.01" placeholder="Rate" value={c.rate} onChange={(e) => updateCustomLine(i, { rate: e.target.value })} className="col-span-3" />
                  <button onClick={() => removeCustomLine(i)} className="col-span-1 text-red-300 hover:text-gray-900 flex justify-center"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></button>
                </div>
              ))}
            </div>

            {/* Discount & Validity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
              <div>
                <label className="text-xs text-gray-500">Discount %</label>
                <Input type="number" min="0" max="100" step="0.01" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} data-testid="qf-discount" />
              </div>
              <div>
                <label className="text-xs text-gray-500">Valid for (days)</label>
                <Input type="number" min="1" max="365" value={form.valid_days} onChange={(e) => setForm({ ...form, valid_days: e.target.value })} data-testid="qf-validity" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs text-gray-500">Notes</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="qf-notes" />
            </div>

            {/* Preview totals */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm" data-testid="qf-preview">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div><span className="text-gray-500">Plan:</span> <b>₹ {preview.planAmt.toLocaleString("en-IN")}</b></div>
                <div><span className="text-gray-500">Modules:</span> <b>₹ {preview.mTotal.toLocaleString("en-IN")}</b></div>
                <div><span className="text-gray-500">Custom:</span> <b>₹ {preview.cTotal.toLocaleString("en-IN")}</b></div>
                <div><span className="text-gray-500">Discount:</span> <b>- ₹ {preview.disc.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b></div>
                <div><span className="text-gray-500">Subtotal:</span> <b>₹ {preview.sub.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b></div>
                <div><span className="text-gray-500">GST 18%:</span> <b>₹ {preview.gst.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b></div>
                <div className="col-span-2 md:col-span-2 text-right">
                  <span className="text-gray-600 text-sm">Total: </span>
                  <span className="text-2xl font-extrabold text-orange-300">₹ {preview.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1 border-white/15">Cancel</Button>
              <Button disabled={saving} onClick={submit} className="flex-1 f360-gradient border-0" data-testid="qf-save-btn">
                {saving ? "Saving…" : editing ? "Update Quote" : "Create Quote"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)}>
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative w-full max-w-3xl bg-white border border-gray-200 rounded-lg p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="quote-detail-modal">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-orange-400">Quotation</div>
                <h3 className="text-2xl font-bold">{detail.quote_number}</h3>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${STATUS_COLOR[detail.status]}`}>{STATUS_LABEL[detail.status]}</span>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-gray-900"><X className="w-5 h-5" strokeWidth={2.5} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Customer</div>
                <div className="font-bold">{detail.customer?.name}</div>
                <div className="text-xs text-gray-500">{detail.customer?.address} {detail.customer?.city} {detail.customer?.pin}</div>
                <div className="text-xs text-gray-500">GSTIN: {detail.customer?.gstin || "—"}</div>
                <div className="text-xs text-gray-500">{detail.customer?.email} · {detail.customer?.phone}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1">Plan</div>
                <div className="font-bold">{detail.plan_name || "—"}</div>
                <div className="text-xs text-gray-500">Cycle: {detail.billing_cycle}</div>
                <div className="text-xs text-gray-500">Valid till: {detail.valid_until ? new Date(detail.valid_until).toLocaleDateString() : "—"}</div>
                {detail.converted_invoice_number && (
                  <div className="text-xs text-purple-300 mt-1">→ Invoice {detail.converted_invoice_number}</div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto mb-3">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                  <tr><th className="text-left py-1">Item</th><th className="text-right py-1">Qty</th><th className="text-right py-1">Rate</th><th className="text-right py-1">Amount</th></tr>
                </thead>
                <tbody>
                  {detail.plan_name && detail.plan_amount > 0 && (
                    <tr className="border-b border-gray-200"><td className="py-1">{detail.plan_name} Plan</td><td className="py-1 text-right">1</td><td className="py-1 text-right">₹ {detail.plan_amount.toLocaleString("en-IN")}</td><td className="py-1 text-right">₹ {detail.plan_amount.toLocaleString("en-IN")}</td></tr>
                  )}
                  {(detail.modules || []).map((m, i) => (
                    <tr key={`m${i}`} className="border-b border-gray-200"><td className="py-1">{m.name}</td><td className="py-1 text-right">{m.units}</td><td className="py-1 text-right">₹ {m.rate.toLocaleString("en-IN")}</td><td className="py-1 text-right">₹ {m.amount.toLocaleString("en-IN")}</td></tr>
                  ))}
                  {(detail.custom_items || []).map((c, i) => (
                    <tr key={`c${i}`} className="border-b border-gray-200"><td className="py-1">{c.description}</td><td className="py-1 text-right">{c.quantity}</td><td className="py-1 text-right">₹ {c.rate.toLocaleString("en-IN")}</td><td className="py-1 text-right">₹ {c.amount.toLocaleString("en-IN")}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-sm mb-4">
              {detail.totals && (
                <div className="flex flex-col gap-1">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>₹ {detail.totals.raw_subtotal.toLocaleString("en-IN")}</span></div>
                  {detail.totals.discount_amount > 0 && (
                    <div className="flex justify-between text-amber-300"><span>Discount ({detail.totals.discount_pct}%)</span><span>- ₹ {detail.totals.discount_amount.toLocaleString("en-IN")}</span></div>
                  )}
                  <div className="flex justify-between"><span className="text-gray-500">GST 18%</span><span>₹ {detail.totals.gst_amount.toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between font-bold text-lg text-orange-300 border-t border-gray-200 pt-1 mt-1"><span>Total</span><span><IndianRupee className="w-4 h-4 inline" strokeWidth={2.5} />{detail.totals.total.toLocaleString("en-IN")}</span></div>
                </div>
              )}
            </div>

            {detail.notes && <div className="text-xs text-gray-500 mb-3 italic">Notes: {detail.notes}</div>}

            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="border-white/15" onClick={() => downloadPDF(detail)} data-testid="detail-pdf-btn">
                <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Download PDF
              </Button>
              <Button variant="outline" className="border-white/15" onClick={() => copyShareLink(detail)} data-testid="detail-share-btn">
                <LinkIcon className="w-4 h-4 mr-1" /> Copy share link
              </Button>
              {detail.status === "draft" && (
                <>
                  <Button variant="outline" className="border-white/15" onClick={() => { setDetail(null); openEdit(detail); }}>Edit</Button>
                  <Button className="f360-gradient border-0" onClick={() => transition(detail.quote_id, "send")} data-testid="detail-send-btn">
                    <Send className="w-4 h-4 mr-1" /> Send
                  </Button>
                </>
              )}
              {detail.status === "sent" && (
                <>
                  <Button className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/30" onClick={() => transition(detail.quote_id, "accept")} data-testid="detail-accept-btn"><Check className="w-4 h-4 mr-1" strokeWidth={2.5} />Accept</Button>
                  <Button className="bg-red-500/20 text-red-200 border border-red-500/30" onClick={() => transition(detail.quote_id, "reject")} data-testid="detail-reject-btn"><X className="w-4 h-4 mr-1" strokeWidth={2.5} />Reject</Button>
                </>
              )}
              {detail.status === "accepted" && (
                <Button className="bg-purple-500/20 text-purple-200 border border-purple-500/30" onClick={() => transition(detail.quote_id, "convert")} data-testid="detail-convert-btn">
                  <ArrowRight className="w-4 h-4 mr-1" strokeWidth={2.5} /> Convert to Invoice
                </Button>
              )}
            </div>

            {shareLink && (
              <div className="mt-3 text-xs text-gray-500 break-all bg-black/30 p-2 rounded">
                Public link: <a href={shareLink} target="_blank" rel="noreferrer" className="text-orange-300">{shareLink}</a>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
